import React, { useState, useEffect } from 'react';
import { Spinner, Alert, Button, Card, Badge, Modal, Form, Row, Col } from 'react-bootstrap';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import {
  getSubscriptionPlans,
  createUserSubscription,
  cancelUserSubscription
} from '../../api/profile';

export const SubscriptionsTab = () => {
  const [loading, setLoading] = useState(true);
  const [plans, setPlans] = useState([]);
  const [mySubscriptions, setMySubscriptions] = useState([]);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);

  // Checkout Modal State
  const [showCheckout, setShowCheckout] = useState(false);
  const [checkoutPlan, setCheckoutPlan] = useState(null);
  const [submittingSubscription, setSubmittingSubscription] = useState(false);

  // Cancellation Modal State
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [subscriptionToCancel, setSubscriptionToCancel] = useState(null);
  const [cancelAtPeriodEnd, setCancelAtPeriodEnd] = useState(true);
  const [submittingCancellation, setSubmittingCancellation] = useState(false);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await getSubscriptionPlans();
      if (res && res.data) {
        setPlans(res.data.plans || []);
        setMySubscriptions(res.data.my_subscriptions || []);
      }
    } catch (err) {
      console.error('Error fetching subscription data:', err);
      setError(err.response?.data?.message || 'Failed to retrieve subscription plans.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleOpenCheckout = (plan) => {
    setCheckoutPlan(plan);
    setShowCheckout(true);
  };

  const handleCloseCheckout = () => {
    setShowCheckout(false);
    setCheckoutPlan(null);
  };

  const handleSubscribeFreeOrTrial = async () => {
    if (!checkoutPlan) return;
    try {
      setSubmittingSubscription(true);
      setError(null);
      setSuccessMsg(null);

      const res = await createUserSubscription(checkoutPlan._id, 'free-trial-ref');
      setSuccessMsg(res.message || `Successfully subscribed to ${checkoutPlan.name}!`);
      handleCloseCheckout();
      await fetchData();
    } catch (err) {
      console.error('Error subscribing:', err);
      setError(err.response?.data?.message || 'Failed to process subscription.');
    } finally {
      setSubmittingSubscription(false);
    }
  };

  const handlePaymentSuccess = async (chargeId) => {
    if (!checkoutPlan) return;
    try {
      setSubmittingSubscription(true);
      setError(null);
      setSuccessMsg(null);

      const res = await createUserSubscription(checkoutPlan._id, chargeId);
      setSuccessMsg(res.message || `Successfully subscribed to ${checkoutPlan.name}!`);
      handleCloseCheckout();
      await fetchData();
    } catch (err) {
      console.error('Error completing subscription activation:', err);
      setError(err.response?.data?.message || 'Payment succeeded, but activation failed. Please contact support.');
    } finally {
      setSubmittingSubscription(false);
    }
  };

  const handleOpenCancel = (sub) => {
    setSubscriptionToCancel(sub);
    setCancelAtPeriodEnd(true);
    setShowCancelModal(true);
  };

  const handleCloseCancel = () => {
    setShowCancelModal(false);
    setSubscriptionToCancel(null);
  };

  const handleConfirmCancel = async () => {
    if (!subscriptionToCancel) return;
    try {
      setSubmittingCancellation(true);
      setError(null);
      setSuccessMsg(null);

      const res = await cancelUserSubscription(subscriptionToCancel.user);
      setSuccessMsg(res.message || 'Subscription cancelled successfully.');
      handleCloseCancel();
      await fetchData();
    } catch (err) {
      console.error('Error cancelling subscription:', err);
      setError(err.response?.data?.message || 'Failed to cancel subscription.');
    } finally {
      setSubmittingCancellation(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="text-muted mt-2">Loading subscription details...</p>
      </div>
    );
  }

  // Active / trial subscriptions
  const activeSubs = mySubscriptions.filter(sub => sub.status === 'active' || sub.status === 'trial');
  // Past / inactive subscriptions
  const inactiveSubs = mySubscriptions.filter(sub => sub.status === 'cancelled' || sub.status === 'expired');

  return (
    <div className="subscriptions-tab-container">
      {successMsg && (
        <Alert variant="success" onClose={() => setSuccessMsg(null)} dismissible className="rounded-3 shadow-sm mb-4">
          <i className="bi bi-check-circle-fill me-2"></i>{successMsg}
        </Alert>
      )}
      {error && (
        <Alert variant="danger" onClose={() => setError(null)} dismissible className="rounded-3 shadow-sm mb-4">
          <i className="bi bi-exclamation-triangle-fill me-2"></i>{error}
        </Alert>
      )}

      {/* SECTION 1: Active Subscriptions */}
      <div className="mb-5">
        <h4 className="fw-bold text-gray-900 mb-3"><i className="bi bi-patch-check text-primary me-2"></i>My Subscriptions</h4>
        {activeSubs.length === 0 ? (
          <Card className="border-0 shadow-sm rounded-4 p-4 text-center bg-white border-start border-4 border-info">
            <Card.Body className="py-4">
              <i className="bi bi-credit-card-2-front fs-1 text-muted opacity-40 mb-3 d-block"></i>
              <h5 className="fw-bold text-gray-800">No Active Subscriptions</h5>
              <p className="text-muted text-sm m-0">Browse our subscription plans below to unlock features, classes, and communities.</p>
            </Card.Body>
          </Card>
        ) : (
          <Row className="g-3">
            {activeSubs.map((sub) => {
              const plan = sub.subscription_plan || {};
              const formattedEndDate = sub.current_period_end
                ? new Date(sub.current_period_end).toLocaleDateString()
                : 'N/A';
              const formattedNextBilling = sub.next_billing_date
                ? new Date(sub.next_billing_date).toLocaleDateString()
                : null;

              return (
                <Col md={6} key={sub._id}>
                  <Card className="active-subscription-card border-0 shadow-sm rounded-4 h-100 position-relative overflow-hidden bg-white">
                    <div className="position-absolute top-0 end-0 p-3">
                      <Badge bg={sub.status === 'trial' ? 'warning' : 'success'} className="px-3 py-1.5 rounded-pill text-capitalize">
                        {sub.status}
                      </Badge>
                    </div>
                    <Card.Body className="p-4 d-flex flex-column h-100">
                      <div className="mb-3">
                        <Badge bg="primary" bg-opacity-10 text="primary" className="text-uppercase text-xs px-2.5 py-1 mb-2 rounded-pill">
                          {Array.isArray(plan.type) ? plan.type.join(', ') : plan.type || 'Plan'}
                        </Badge>
                        <h5 className="fw-bold text-gray-900 m-0">{plan.name || 'Subscription Plan'}</h5>
                        {plan.price !== undefined && (
                          <div className="mt-1 fw-bold text-primary fs-5">
                            ${plan.price > 100 ? (plan.price / 100).toFixed(2) : plan.price.toFixed(2)} USD
                            <span className="text-muted font-normal text-xs"> / {plan.payment_frequency}</span>
                          </div>
                        )}
                      </div>

                      <div className="info-grid py-3 border-top border-bottom my-3">
                        <Row className="g-2 text-sm">
                          <Col xs={6}>
                            <span className="text-muted d-block text-xs">Started on</span>
                            <span className="fw-semibold text-gray-800">{sub.current_period_start ? new Date(sub.current_period_start).toLocaleDateString() : 'N/A'}</span>
                          </Col>
                          <Col xs={6}>
                            <span className="text-muted d-block text-xs">Expires/Renews</span>
                            <span className="fw-semibold text-gray-800">{formattedEndDate}</span>
                          </Col>
                          {formattedNextBilling && !sub.cancel_at_period_end && (
                            <Col xs={12} className="mt-2">
                              <span className="text-muted d-block text-xs">Next Billing Date</span>
                              <span className="fw-semibold text-gray-800"><i className="bi bi-calendar3 me-1 text-primary"></i>{formattedNextBilling}</span>
                            </Col>
                          )}
                          {sub.cancel_at_period_end && (
                            <Col xs={12} className="mt-2 text-danger">
                              <i className="bi bi-exclamation-triangle-fill me-1"></i>
                              <span>Pending cancellation at end of period</span>
                            </Col>
                          )}
                        </Row>
                      </div>

                      {/* <div className="mt-auto d-flex justify-content-end align-items-center gap-2">
                        {!sub.cancel_at_period_end && (
                          <Button
                            variant="outline-danger"
                            size="sm"
                            className="px-3 rounded-pill fw-bold"
                            onClick={() => handleOpenCancel(sub)}
                          >
                            Cancel Subscription
                          </Button>
                        )}
                      </div> */}
                    </Card.Body>
                  </Card>
                </Col>
              );
            })}
          </Row>
        )}
      </div>

      {/* SECTION 2: Available Subscription Plans */}
      <div className="mb-5">
        <h4 className="fw-bold text-gray-900 mb-3"><i className="bi bi-grid-3x3-gap text-primary me-2"></i>Available Plans</h4>
        {plans.length === 0 ? (
          <p className="text-muted">No subscription plans are currently available for subscription.</p>
        ) : (
          <Row className="g-4">
            {plans.map((plan) => {
              const displayPrice = plan.price > 100 ? (plan.price / 100).toFixed(2) : plan.price.toFixed(2);
              const isSubscribed = plan.subscribed;
              const features = plan.features || [];

              return (
                <Col md={4} key={plan._id}>
                  <Card className={`plan-pricing-card border-0 shadow-sm rounded-4 h-100 bg-white d-flex flex-column overflow-hidden ${isSubscribed ? 'plan-subscribed border-top border-4 border-primary' : ''}`}>
                    {isSubscribed && (
                      <div className="subscribed-tag text-center bg-primary text-white text-xs py-1 fw-bold">
                        ACTIVE SUBSCRIPTION
                      </div>
                    )}
                    <Card.Body className="p-4 d-flex flex-column h-100">
                      <div className="mb-3">
                        <Badge bg="secondary" bg-opacity-10 text="secondary" className="text-uppercase text-xs px-2.5 py-1 mb-2 rounded-pill">
                          {Array.isArray(plan.type) ? plan.type.join(', ') : plan.type || 'Plan'}
                        </Badge>
                        <h5 className="fw-bold text-gray-900 mb-1">{plan.name}</h5>
                        <p className="text-muted text-xs mb-3 text-truncate-2" style={{ height: '36px', overflow: 'hidden' }}>{plan.description}</p>
                      </div>

                      <div className="price-tag mb-4 py-3 border-top border-bottom">
                        <span className="fs-2 fw-black text-gray-900">${displayPrice}</span>
                        <span className="text-muted text-sm text-capitalize"> / {plan.payment_frequency}</span>
                        {plan.trial_days > 0 && (
                          <div className="text-xs text-success fw-semibold mt-1">
                            <i className="bi bi-gift-fill me-1"></i>Includes {plan.trial_days} days trial
                          </div>
                        )}
                      </div>

                      <div className="features-list mb-4 flex-grow-1">
                        <h6 className="fw-bold text-gray-700 text-xs mb-2">FEATURES INCLUDED:</h6>
                        <ul className="list-unstyled text-sm text-muted m-0">
                          {features.length === 0 ? (
                            <li><i className="bi bi-check2 text-primary me-2"></i>Access to select contents</li>
                          ) : (
                            features.map((feat, i) => (
                              <li key={i} className="mb-2 d-flex align-items-start">
                                <i className="bi bi-check-circle-fill text-primary me-2 mt-0.5" style={{ minWidth: '16px' }}></i>
                                <span>{feat}</span>
                              </li>
                            ))
                          )}
                        </ul>
                      </div>

                      <div className="mt-auto pt-3">
                        <Button
                          variant={isSubscribed ? "outline-primary" : "primary"}
                          className="w-100 py-2.5 rounded-pill fw-bold"
                          disabled={isSubscribed}
                          onClick={() => handleOpenCheckout(plan)}
                        >
                          {isSubscribed ? 'Subscribed' : 'Subscribe Now'}
                        </Button>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              );
            })}
          </Row>
        )}
      </div>

      {/* SECTION 3: Past Subscriptions (History) */}
      {inactiveSubs.length > 0 && (
        <div>
          <h5 className="fw-bold text-gray-800 mb-3">Billing History & Past Subscriptions</h5>
          <div className="table-responsive border rounded-3 bg-white">
            <table className="table table-hover align-middle mb-0 text-sm">
              <thead className="table-light">
                <tr>
                  <th>Plan Name</th>
                  <th>Type</th>
                  <th>Frequency</th>
                  <th>Price Paid</th>
                  <th>Status</th>
                  <th>Period</th>
                </tr>
              </thead>
              <tbody>
                {inactiveSubs.map((sub) => {
                  const plan = sub.subscription_plan || {};
                  return (
                    <tr key={sub._id}>
                      <td><span className="fw-bold text-gray-900">{plan.name || 'N/A'}</span></td>
                      <td><span className="text-capitalize text-xs text-muted">{Array.isArray(plan.type) ? plan.type.join(', ') : plan.type || 'N/A'}</span></td>
                      <td><span className="text-capitalize text-xs text-muted">{plan.payment_frequency || 'N/A'}</span></td>
                      <td><span>${sub.amount_paid ? sub.amount_paid.toFixed(2) : '0.00'}</span></td>
                      <td>
                        <Badge bg={sub.status === 'expired' ? 'secondary' : 'danger'} className="text-capitalize rounded-pill">
                          {sub.status}
                        </Badge>
                      </td>
                      <td>
                        <span className="text-xs text-muted">
                          {sub.current_period_start ? new Date(sub.current_period_start).toLocaleDateString() : ''} - {sub.current_period_end ? new Date(sub.current_period_end).toLocaleDateString() : ''}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* MODAL 1: Checkout Subscription */}
      <Modal show={showCheckout} onHide={handleCloseCheckout} centered className="checkout-subscription-modal">
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="fw-black text-gray-900">Checkout Subscription</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4 pt-2">
          {checkoutPlan && (
            <div>
              <div className="checkout-summary-card p-3 rounded-3 mb-4 bg-light">
                <Badge bg="primary" className="mb-2 text-uppercase text-xs rounded-pill">
                  {Array.isArray(checkoutPlan.type) ? checkoutPlan.type.join(', ') : checkoutPlan.type}
                </Badge>
                <h5 className="fw-bold text-gray-900 mb-1">{checkoutPlan.name}</h5>
                <p className="text-muted text-xs mb-3">{checkoutPlan.description}</p>
                <div className="d-flex justify-content-between align-items-end border-top pt-2 mt-2">
                  <span className="text-sm fw-medium text-gray-700">Billing frequency:</span>
                  <span className="text-capitalize fw-bold text-gray-800">{checkoutPlan.payment_frequency}</span>
                </div>
                <div className="d-flex justify-content-between align-items-end border-top pt-2 mt-2">
                  <span className="fs-5 fw-bold text-gray-700">Price:</span>
                  <span className="fs-4 fw-black text-primary">${checkoutPlan.price > 100 ? (checkoutPlan.price / 100).toFixed(2) : checkoutPlan.price.toFixed(2)} USD</span>
                </div>
              </div>

              {checkoutPlan.price === 0 || (checkoutPlan.trial_days > 0 && !submittingSubscription) ? (
                <div>
                  <p className="text-muted text-xs mb-4">
                    This plan includes a free trial. You will not be charged immediately. You can confirm your subscription below.
                  </p>
                  <Button
                    onClick={handleSubscribeFreeOrTrial}
                    disabled={submittingSubscription}
                    className="w-100 py-2.5 rounded-pill fw-bold"
                  >
                    {submittingSubscription ? <><Spinner animation="border" size="sm" className="me-2" />Processing...</> : 'Subscribe for Free / Trial'}
                  </Button>
                </div>
              ) : (
                <div>
                  <p className="text-muted text-xs mb-4">
                    Please complete payment via PayPal below to activate your premium subscription. Your billing starts immediately.
                  </p>

                  {submittingSubscription ? (
                    <div className="text-center py-4">
                      <Spinner animation="border" variant="primary" />
                      <p className="text-muted text-sm mt-2">Activating your subscription, please do not close this window...</p>
                    </div>
                  ) : (
                    <div className="paypal-checkout-container">
                      <PayPalScriptProvider options={{ "client-id": "AcEBlYxSTDKF51XKtEB_L4-X18Q4kpoyUj0yY6DggYqVCssqh9nJbcUESyx6Fp_HLJnPy3msNxmjttaG", currency: "USD" }}>
                        <PayPalButtons
                          style={{ layout: "vertical", color: "blue", shape: "pill", label: "pay" }}
                          createOrder={(data, actions) => {
                            const value = checkoutPlan.price > 100 ? (checkoutPlan.price / 100).toString() : checkoutPlan.price.toString();
                            return actions.order.create({
                              purchase_units: [{
                                amount: { value: value },
                                description: `YooSpeak Subscription: ${checkoutPlan.name}`
                              }]
                            });
                          }}
                          onApprove={(data, actions) => {
                            return actions.order.capture().then(async (details) => {
                              await handlePaymentSuccess(details.id);
                            });
                          }}
                          onError={(err) => {
                            console.error('PayPal checkout error:', err);
                            setError('PayPal checkout encountered an error. Please try again.');
                            handleCloseCheckout();
                          }}
                        />
                      </PayPalScriptProvider>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </Modal.Body>
      </Modal>

      {/* MODAL 2: Cancel Subscription */}
      <Modal show={showCancelModal} onHide={handleCloseCancel} centered className="cancel-subscription-modal">
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="fw-bold text-danger"><i className="bi bi-exclamation-triangle-fill me-2"></i>Cancel Subscription</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4 pt-2">
          {subscriptionToCancel && (
            <div>
              <p className="text-muted text-sm mb-4">
                Are you sure you want to cancel your subscription to <strong>{subscriptionToCancel.subscription_plan?.name}</strong>?
              </p>

              <Form.Group className="mb-4">
                <Form.Label className="fw-bold text-gray-700 mb-2">Select Cancellation Type</Form.Label>
                <div className="p-3 border rounded-3 bg-light">
                  <Form.Check
                    type="radio"
                    id="cancel-period-end"
                    name="cancellation-type"
                    label={
                      <div>
                        <span className="fw-bold text-gray-800">Cancel at the end of billing period (Recommended)</span>
                        <span className="text-muted text-xs d-block mt-0.5">
                          You will keep access to premium features until the end of your billing cycle (expires on {subscriptionToCancel.current_period_end ? new Date(subscriptionToCancel.current_period_end).toLocaleDateString() : 'N/A'}).
                        </span>
                      </div>
                    }
                    checked={cancelAtPeriodEnd}
                    onChange={() => setCancelAtPeriodEnd(true)}
                    className="mb-3 d-flex align-items-start gap-1"
                  />
                  <Form.Check
                    type="radio"
                    id="cancel-immediate"
                    name="cancellation-type"
                    label={
                      <div>
                        <span className="fw-bold text-danger">Cancel immediately</span>
                        <span className="text-muted text-xs d-block mt-0.5">
                          Your subscription is terminated immediately. You will lose access to premium benefits right away.
                        </span>
                      </div>
                    }
                    checked={!cancelAtPeriodEnd}
                    onChange={() => setCancelAtPeriodEnd(false)}
                    className="d-flex align-items-start gap-1"
                  />
                </div>
              </Form.Group>

              <div className="d-flex justify-content-end gap-2 border-top pt-3">
                <Button variant="outline-secondary" onClick={handleCloseCancel} disabled={submittingCancellation} className="px-4 rounded-pill">
                  Keep Subscription
                </Button>
                <Button variant="danger" onClick={handleConfirmCancel} disabled={submittingCancellation} className="px-4 rounded-pill fw-bold">
                  {submittingCancellation ? <><Spinner animation="border" size="sm" className="me-2" />Processing...</> : 'Cancel Subscription'}
                </Button>
              </div>
            </div>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};
